import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAvailability } from '../hooks/useAvailability';
import AvailabilityGrid from '../components/AvailabilityGrid';
import Button from '../components/ui/Button';

/**
 * Page for setting user availability
 */
const AvailabilityPage = () => {
  const { user, logout: authContextLogout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get class type from location state or default to 'mandarin'
  const classType = location.state?.classType || 'mandarin';
  
  // Use availability hook
  const { 
    availability, 
    setAvailability,
    loading,
    error,
    hasSelections
  } = useAvailability();
  
  // Handle availability change
  const handleAvailabilityChange = (newAvailability) => {
    setAvailability(newAvailability);
  };
  
  // Handle back button
  const handleBack = () => {
    navigate('/classes');
  };
  
  // Handle submit
  const handleSubmit = () => {
    if (!hasSelections) {
      alert('Please select at least one time slot');
      return;
    }
  
    // Get classTypeId from location state or URL params
    const classTypeId = location.state?.classTypeId || sessionStorage.getItem('selectedClassTypeId');
    
    // Navigate to confirmation page
    navigate('/confirmation', {
      state: {
        classTypeId,
        classType,
        availability,
        mode: 'availability'
      }
    });
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      if (authContextLogout) {
        await authContextLogout();
      }
      navigate('/classes');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-950 text-white">
      {/* Header - matching other pages */}
      <header className="bg-gray-900 border-b border-gray-800 sticky top-0 z-10 shadow-md">
        <div className="container mx-auto p-4 flex justify-between items-center">
          <h1 className="text-xl font-bold">
            <span className="bg-gradient-to-r from-purple-400 to-indigo-500 text-transparent bg-clip-text">
              Join a Group Class
            </span>
          </h1>
          
          <div className="flex items-center">
            <span className="text-gray-300 text-sm hidden sm:inline mr-4">
              Hello, {user?.name}
            </span>
            <Button
              variant="outline"
              size="small"
              onClick={handleLogout}
              className="border-gray-600 hover:bg-gray-700"
            >
              Logout
            </Button>
          </div>
        </div>
        {/* Add decorative accent line */}
        <div className="h-1 bg-gradient-to-r from-purple-600 via-indigo-500 to-purple-600"></div>
      </header>
      
      {/* Main content */}
      <main className="container mx-auto py-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Title with Chinese character decoration - matching other pages */}
          <div className="text-center mb-8">
            <div className="inline-block text-6xl font-light text-purple-400 opacity-20 mb-2">
              设置时间
            </div>
            <h2 className="text-2xl font-bold">
              Select Your Availability for a {classType.charAt(0).toUpperCase() + classType.slice(1)} Class
            </h2>
            <div className="w-16 h-1 bg-purple-500 mx-auto mt-2"></div>
          </div>
          
          {error && (
            <div className="p-4 bg-red-900 bg-opacity-20 border border-red-700 rounded-lg mb-6">
              <p className="text-red-400">{error}</p>
            </div>
          )}
          
          {/* Availability grid */}
          <div className="bg-gray-800 rounded-lg border border-gray-700 shadow-lg overflow-hidden mb-8">
            <div className="bg-gradient-to-r from-gray-800 to-gray-700 px-6 py-4 border-b border-gray-700">
              <h3 className="font-semibold text-lg">Tap what times you'd be free</h3>
              <p className="text-sm text-gray-400 mt-2">
                We'll email you when someone matches with your available times to form a new class.
              </p>
            </div>
            <div className="p-6">
              <AvailabilityGrid
                availability={availability}
                onChange={handleAvailabilityChange}
                classType={classType}
              />
            </div>
          </div>
          
          {/* Action buttons */}
          <div className="flex justify-between">
            <Button
              variant="secondary"
              onClick={handleBack}
              className="bg-gray-700 hover:bg-gray-600 border border-gray-600"
            >
              Back
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={!hasSelections || loading}
              isLoading={loading}
              className="bg-purple-600 hover:bg-purple-700"
            >
              Submit Availability
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AvailabilityPage;